"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MobileDepositFlow } from "@/components/mobile/mobile-deposit-flow"
import { generateDepositToken, generateWithdrawalToken } from "@/lib/services/partners-api"
import { MobileWithdrawFlow } from "@/components/mobile/mobile-withdraw-flow"
import {
  Wallet,
  CheckCircle,
  History,
  Calendar,
  TrendingUp,
  DollarSign,
  Activity,
  ArrowDown,
  ArrowUp,
  BookOpen,
} from "lucide-react"

interface CasinoDashboardProps {
  verifiedEmail?: string
  shouldStartDeposit?: boolean
  orderData?: any // Datos completos de la orden del token
}

export function CasinoDashboard({ verifiedEmail, shouldStartDeposit, orderData }: CasinoDashboardProps = {}) {
  const router = useRouter()
  const [showDepositFlow, setShowDepositFlow] = useState(false)
  const [showWithdrawFlow, setShowWithdrawFlow] = useState(false)
  const [withdrawAmount, setWithdrawAmount] = useState("")
  const [withdrawAddress, setWithdrawAddress] = useState("")
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  const [showWithdrawForm, setShowWithdrawForm] = useState(false) // Declared showWithdrawForm

  const [balance, setBalance] = useState(50)
  const [lastDeposit, setLastDeposit] = useState<{ amount: number; timestamp: Date } | null>(null)
  const [balanceAnimation, setBalanceAnimation] = useState(false)
  const [depositHistory, setDepositHistory] = useState<
    Array<{
      id: string
      amount: number
      timestamp: Date
      status: "completed" | "pending" | "failed"
      method: string
      type: "deposit" | "withdrawal" // Added transaction type
    }>
  >([
    {
      id: "TXN001",
      amount: 25,
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      status: "completed",
      method: "NBC TALK PAY",
      type: "deposit",
    },
    {
      id: "TXN002",
      amount: 50,
      timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
      status: "completed",
      method: "NBC TALK PAY",
      type: "deposit",
    },
  ])

  const [loyaltyPoints, setLoyaltyPoints] = useState(125)
  const [userLevel, setUserLevel] = useState("Silver")
  const [achievements, setAchievements] = useState<Array<{
    id: string;
    name: string;
    icon: string;
    unlocked: boolean;
    date?: string;
    requirement?: string;
  }>>([
    { id: "first_deposit", name: "First Deposit", icon: "🎯", unlocked: true, date: "2024-01-15" },
    { id: "loyal_player", name: "Loyal Player", icon: "💎", unlocked: true, date: "2024-01-20" },
    { id: "high_roller", name: "High Roller", icon: "👑", unlocked: false, requirement: "Deposit $500+" },
  ])

  const handleDepositComplete = (amount: number) => {
    const depositAmount = Number(amount) || 0
    setBalanceAnimation(true)
    setTimeout(() => setBalanceAnimation(false), 1000)

    setBalance((prev) => Number(prev) + depositAmount)
    setLastDeposit({ amount: depositAmount, timestamp: new Date() })

    const pointsEarned = Math.floor(depositAmount * 0.1) // 0.1 points per dollar
    setLoyaltyPoints((prev) => prev + pointsEarned)

    const newDeposit = {
      id: `TXN${String(depositHistory.length + 3).padStart(3, "0")}`,
      amount: depositAmount,
      timestamp: new Date(),
      status: "completed" as const,
      method: "NBC TALK PAY",
      type: "deposit" as const, // Added type for new deposits
    }
    setDepositHistory((prev) => [newDeposit, ...prev])
    setShowDepositFlow(false)

    if (depositAmount >= 500 && !achievements.find((a) => a.id === "high_roller")?.unlocked) {
      setAchievements((prev) =>
        prev.map((a) =>
          a.id === "high_roller" ? { ...a, unlocked: true, date: new Date().toISOString().split("T")[0] } : a,
        ),
      )
    }
  }

  // Manejar email verificado desde el flujo de tokens
  useEffect(() => {
    if (verifiedEmail && shouldStartDeposit) {
      console.log('✅ Email verified, starting deposit flow:', verifiedEmail)
      console.log('📋 Order data available:', orderData)
      
      // Automáticamente abrir el flujo de depósito con el email verificado
      setShowDepositFlow(true)
      
      // Limpiar la URL para evitar reprocessamiento
      router.replace('/', { scroll: false })
    }
  }, [verifiedEmail, shouldStartDeposit, orderData, router])

  const handleWithdrawComplete = (amount: number) => {
    setBalance((prev) => prev - amount)
    setBalanceAnimation(true)
    setTimeout(() => setBalanceAnimation(false), 1000)

    const newWithdrawal = {
      id: `WTH${String(depositHistory.length + 3).padStart(3, "0")}`,
      amount: -amount,
      timestamp: new Date(),
      status: "completed" as const,
      method: "NBC TALK PAY",
      type: "withdrawal" as const,
    }
    setDepositHistory((prev) => [newWithdrawal, ...prev])
    setShowWithdrawFlow(false)
  }

  const handleDepositClick = async () => {
    try {
      console.log('🚀 Starting NBC TALK PAY deposit flow...')
      
      const email = 'player@casino.com' // Email del jugador (Casino Ivan NBCBANQ)
      const apiKey = 'a94b2628-3c5e-47b4-97e3-53007094bb3b' // API Key de Casino Ivan NBCBANQ
      
      // 1. Crear orden de depósito
      const orderFormData = new FormData()
      orderFormData.append('email', email)
      orderFormData.append('amount', '50') // Cantidad por defecto
      orderFormData.append('partner_order_id', `CASINO-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`)
      orderFormData.append('callback_url', `${window.location.origin}/api/webhook/nbc`)
      
      // Llamar directamente al backend con la API key
      const orderResponse = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:5000'}/api/partner/create-deposit-order`, {
        method: 'POST',
        headers: {
          'X-Partner-API-Key': apiKey
        },
        body: orderFormData
      })
      
      if (!orderResponse.ok) {
        const errorData = await orderResponse.json()
        throw new Error(errorData.error || 'Failed to create deposit order')
      }
      
      const orderData = await orderResponse.json()
      console.log('✅ Deposit order created:', orderData)
      
      // 2. Generar token seguro llamando directamente al backend
      const tokenFormData = new FormData()
      tokenFormData.append('order_id', orderData.order_id)
      tokenFormData.append('email', email)
      tokenFormData.append('expiration_seconds', '300') // 5 minutos
      
      const tokenResponse = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:5000'}/api/partner/generate-deposit-token`, {
        method: 'POST',
        headers: {
          'X-Partner-API-Key': apiKey
        },
        body: tokenFormData
      })
      
      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json()
        throw new Error(errorData.error || 'Failed to generate token')
      }
      
      const tokenData = await tokenResponse.json()
      console.log('✅ Secure token generated:', {
        token: tokenData.token,
        expires_at: tokenData.expires_at
      })
      
      // 3. Redirigir con token seguro
      console.log('🔄 Redirecting to:', tokenData.redirect_url)
      window.location.href = tokenData.redirect_url
      
    } catch (error) {
      console.error('❌ Error iniciando depósito NBC TALK PAY:', error)
      
      // Fallback al flujo anterior si falla
      console.log('🔄 Falling back to direct deposit flow...')
      setShowDepositFlow(true)
    }
  }

  const handleWithdrawClick = async () => {
    try {
      console.log('🚀 Starting NBC TALK PAY withdrawal flow...')
      
      const email = 'player@casino.com' // Email del jugador (Casino Ivan NBCBANQ)
      const apiKey = 'a94b2628-3c5e-47b4-97e3-53007094bb3b' // API Key de Casino Ivan NBCBANQ
      
      // 1. Crear orden de retiro
      const orderFormData = new FormData()
      orderFormData.append('email', email)
      orderFormData.append('amount', '25') // Cantidad por defecto (puede ser modificada en el flujo)
      orderFormData.append('partner_order_id', `CASINO-WTH-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`)
      orderFormData.append('callback_url', `${window.location.origin}/api/webhook/nbc`)
      orderFormData.append('user_balance', balance.toString()) // Balance actual del usuario en el casino
      
      // Llamar directamente al backend con la API key
      const orderResponse = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:5000'}/api/partner/create-withdrawal-order`, {
        method: 'POST',
        headers: {
          'X-Partner-API-Key': apiKey
        },
        body: orderFormData
      })
      
      if (!orderResponse.ok) {
        const errorData = await orderResponse.json()
        throw new Error(errorData.error || 'Failed to create withdrawal order')
      }
      
      const orderData = await orderResponse.json()
      console.log('✅ Withdrawal order created:', orderData)
      
      // 2. Generar token seguro para withdrawal llamando directamente al backend
      const tokenFormData = new FormData()
      tokenFormData.append('order_id', orderData.order_id)
      tokenFormData.append('email', email)
      tokenFormData.append('expiration_seconds', '300') // 5 minutos
      
      const tokenResponse = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:5000'}/api/partner/generate-withdrawal-token`, {
        method: 'POST',
        headers: {
          'X-Partner-API-Key': apiKey
        },
        body: tokenFormData
      })
      
      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json()
        throw new Error(errorData.error || 'Failed to generate withdrawal token')
      }
      
      const tokenData = await tokenResponse.json()
      console.log('✅ Secure withdrawal token generated:', {
        token: tokenData.token,
        expires_at: tokenData.expires_at
      })
      
      // 3. Redirigir con token seguro al flujo de withdrawal
      console.log('🔄 Redirecting to:', tokenData.redirect_url)
      window.location.href = tokenData.redirect_url
      
    } catch (error) {
      console.error('❌ Error iniciando retiro NBC TALK PAY:', error)
      
      // Fallback al flujo anterior si falla
      console.log('🔄 Falling back to direct withdrawal flow...')
      setShowWithdrawFlow(true)
    }
  }

  const totalDeposits = depositHistory
    .filter((d) => d.type === "deposit")
    .reduce((sum, deposit) => sum + deposit.amount, 0)
  const completedDeposits = depositHistory.filter((d) => d.status === "completed").length

  const nextLevelPoints =
    userLevel === "Bronze" ? 100 : userLevel === "Silver" ? 250 : userLevel === "Gold" ? 500 : 1000
  const levelProgress = (loyaltyPoints / nextLevelPoints) * 100

  if (showDepositFlow) {
    return (
      <MobileDepositFlow
        onComplete={handleDepositComplete}
        userEmail={verifiedEmail || "player@casino.com"}
        onBack={() => setShowDepositFlow(false)}
        prefilledOrderData={orderData}
      />
    )
  }

  if (showWithdrawFlow) {
    return (
      <MobileWithdrawFlow
        onComplete={handleWithdrawComplete}
        onBack={() => setShowWithdrawFlow(false)}
        userBalance={balance}
      />
    )
  }

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Casino Header */}
      <div className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">🎰 Royal Casino</h1>
              <p className="text-purple-200">Welcome back, Player!</p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => router.push('/docs')}
                variant="outline"
                size="sm"
                className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-200 border-purple-400/30 hover:border-purple-400/50 transition-all"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Documentation
              </Button>
              <div className="text-right">
                <p className="text-sm text-purple-200">Player ID</p>
                <p className="text-white font-mono">#PC001</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Balance Card - Now at the top */}
        <Card className="mb-8 bg-slate-800/90 backdrop-blur-sm border-slate-700 transition-all duration-300 hover:shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Casino Balance
            </CardTitle>
            <CardDescription className="text-slate-300">Your available gaming funds</CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`text-4xl font-bold text-white mb-4 transition-all duration-500 ${
                balanceAnimation ? "scale-110 text-green-400" : ""
              }`}
            >
              ${balance.toFixed(2)}
            </div>

            {lastDeposit && (
              <div className="flex items-center gap-2 text-green-400 text-sm mb-4 animate-in slide-in-from-left-2 duration-500">
                <CheckCircle className="h-4 w-4" />
                Last deposit: ${lastDeposit.amount} at {lastDeposit.timestamp.toLocaleTimeString()}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Button
                onClick={handleDepositClick}
                className="hover:bg-gray-100 text-black font-semibold transition-all duration-300 hover:scale-[1.02] hover:shadow-lg bg-sidebar-primary"
                size="lg"
              >
                <div className="flex items-center justify-center gap-2">
                  <ArrowDown className="h-4 w-4" />
                  <span>Deposit with NBC TALK PAY</span>
                </div>
              </Button>

              <Button
                onClick={handleWithdrawClick}
                className="bg-white hover:bg-gray-100 text-black font-semibold border-0 transition-all duration-300 hover:scale-[1.02]"
                size="lg"
                disabled={balance <= 0}
              >
                <div className="flex items-center justify-center gap-2">
                  <ArrowUp className="h-4 w-4" />
                  <span>Withdraw with NBC TALK PAY</span>
                </div>
              </Button>
            </div>

            {showWithdrawForm && (
              <Card className="mt-4 bg-slate-700/50 border-slate-600">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Withdraw Funds</CardTitle>
                  <CardDescription className="text-slate-300">
                    Withdraw your casino balance to your NBC TALK PAY wallet
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="withdraw-amount" className="text-white">
                      Amount (USD)
                    </Label>
                    <Input
                      id="withdraw-amount"
                      type="number"
                      placeholder="0.00"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      max={balance}
                      className="bg-slate-800 border-slate-600 text-white"
                      disabled={isWithdrawing}
                    />
                    <div className="text-sm text-slate-400">Available: ${balance.toFixed(2)} • Min: $10.00</div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="withdraw-address" className="text-white">
                      USDT Address (TRC-20)
                    </Label>
                    <Input
                      id="withdraw-address"
                      placeholder="TQn9Y2khEsLMWn99x9Rm8hLqRQjhUK8FeC"
                      value={withdrawAddress}
                      onChange={(e) => setWithdrawAddress(e.target.value)}
                      className="bg-slate-800 border-slate-600 text-white font-mono text-sm"
                      disabled={isWithdrawing}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleWithdrawComplete(Number.parseFloat(withdrawAmount))}
                      disabled={
                        !withdrawAmount ||
                        !withdrawAddress ||
                        Number.parseFloat(withdrawAmount) < 10 ||
                        Number.parseFloat(withdrawAmount) > balance ||
                        isWithdrawing
                      }
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                    >
                      {isWithdrawing ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Processing...
                        </div>
                      ) : (
                        "Withdraw Funds"
                      )}
                    </Button>
                    <Button
                      onClick={() => setShowWithdrawForm(false)}
                      className="border-slate-600 text-white hover:bg-slate-700"
                      disabled={isWithdrawing}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6 bg-slate-900/95 backdrop-blur-sm border-slate-600 transition-all duration-300 hover:shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-black">⭐</span>
              </div>
              Loyalty Program - {userLevel} Member
            </CardTitle>
            <CardDescription className="text-slate-200">
              Earn points with every deposit and unlock exclusive rewards
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between text-white">
                <span>Loyalty Points</span>
                <span className="text-2xl font-bold text-yellow-300">{loyaltyPoints}</span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-slate-200">
                  <span>Progress to next level</span>
                  <span>
                    {loyaltyPoints}/{nextLevelPoints}
                  </span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-linear-to-r from-yellow-400 to-yellow-500 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${Math.min(levelProgress, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6 bg-slate-800/90 backdrop-blur-sm border-slate-700 transition-all duration-300 hover:shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <div className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center">
                <span className="text-sm">🏆</span>
              </div>
              Achievements
            </CardTitle>
            <CardDescription className="text-slate-300">Your gaming milestones and accomplishments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {achievements.map((achievement, index) => (
                <div
                  key={achievement.id}
                  className={`p-3 rounded-lg border transition-all duration-300 hover:scale-105 ${
                    achievement.unlocked
                      ? "bg-green-900/30 border-green-600 text-green-100"
                      : "bg-slate-700/50 border-slate-600 text-slate-400"
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="text-center space-y-2">
                    <div className="text-2xl">{achievement.icon}</div>
                    <div className="font-medium text-sm">{achievement.name}</div>
                    {achievement.unlocked ? (
                      <div className="text-xs opacity-80">Unlocked {achievement.date}</div>
                    ) : (
                      <div className="text-xs opacity-60">{achievement.requirement}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/80 backdrop-blur-sm border-slate-700 transition-all duration-300 hover:scale-105">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-green-400" />
                <Badge variant="secondary" className="text-xs">
                  Available
                </Badge>
              </div>
              <div className="text-2xl font-bold text-white">${balance.toFixed(2)}</div>
              <div className="text-sm text-slate-300">Current Balance</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/80 backdrop-blur-sm border-slate-700 transition-all duration-300 hover:scale-105">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-blue-400" />
                <Badge variant="secondary" className="text-xs">
                  Total
                </Badge>
              </div>
              <div className="text-2xl font-bold text-white">${totalDeposits.toFixed(2)}</div>
              <div className="text-sm text-slate-300">Total Deposits</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/80 backdrop-blur-sm border-slate-700 transition-all duration-300 hover:scale-105">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-purple-400" />
                <Badge variant="secondary" className="text-xs">
                  Count
                </Badge>
              </div>
              <div className="text-2xl font-bold text-white">{completedDeposits}</div>
              <div className="text-sm text-slate-300">Transactions</div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/80 backdrop-blur-sm border-slate-700 transition-all duration-300 hover:scale-105">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <Badge variant="secondary" className="text-xs">
                  Status
                </Badge>
              </div>
              <div className="text-2xl font-bold text-white">Active</div>
              <div className="text-sm text-slate-300">Account Status</div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-slate-800/90 backdrop-blur-sm border-slate-700 transition-all duration-300 hover:shadow-2xl">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <History className="h-5 w-5" />
              Transaction History
              <Badge className="bg-purple-600 text-white">{depositHistory.length}</Badge>
            </CardTitle>
            <CardDescription className="text-slate-300">Your deposits and withdrawals history</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {depositHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No transactions yet</p>
                </div>
              ) : (
                depositHistory.map((transaction, index) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg border border-slate-600 transition-all duration-300 hover:bg-slate-700/70 hover:scale-[1.02] animate-in slide-in-from-bottom-2"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
                          transaction.type === "deposit" ? "bg-purple-600" : "bg-orange-600"
                        }`}
                      >
                        {transaction.type === "deposit" ? (
                          <ArrowDown className="h-5 w-5 text-white" />
                        ) : (
                          <ArrowUp className="h-5 w-5 text-white" />
                        )}
                      </div>
                      <div>
                        <div className="text-white font-medium">
                          {transaction.type === "deposit" ? "Deposit" : "Withdrawal"} - {transaction.method}
                        </div>
                        <div className="text-sm text-slate-400 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {transaction.timestamp.toLocaleDateString("en-US")} at{" "}
                          {transaction.timestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={`font-bold ${transaction.type === "deposit" ? "text-green-400" : "text-orange-400"}`}
                      >
                        {transaction.type === "deposit" ? "+" : ""}${Math.abs(transaction.amount).toFixed(2)}
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-400" />
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                          {transaction.status}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
